import React from "react";
import Dropzone from 'react-dropzone';
import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";
import { Icon, Loader } from "semantic-ui-react";
import * as mime from "react-native-mime-types";
import { ToastContext } from "@weezevent/nacre";

/**
* Display a drop zone wich can detect type of files or display a Progress bar.
* @returns HTML dropzone or progress bar
*/
export default function DropzoneInput({
    isUploading,
    disabled,
    onFileSelected,
    acceptedFileExtensions,
    ...baseProps
}) {
    const { t } = useTranslation();

    const acceptedMimeTypes = acceptedFileExtensions.map(value => mime.lookup(value));

    /**
     * Action on dropzone to get files accepted and rejected by their type
     * @param accepted
     * @param rejected
     */
    const toast = React.useContext(ToastContext);
    const onDropAccepted = React.useCallback(accepted => {
        onFileSelected(accepted[0]);
    }, [ onFileSelected ]);

    const onDropRejected = React.useCallback((rejected, event) => {
        const actual = rejected[0].type;
        toast.error(t(['nacre.UploadButton.unexpected-filetype', 'Expected a %expected% (%expectedMimeTypes%) file and got %actual% instead'], {
            expected: acceptedFileExtensions,
            expectedMimeTypes: acceptedMimeTypes,
            actual: actual || t(['nacre.UploadButton.unknown-filetype', 'unknown']),
        }));
    }, [ t, toast, acceptedFileExtensions, acceptedMimeTypes ]);

    if (isUploading) {
        return <div><Loader active inline='centered' /></div>;
    }

    return (
        <Dropzone
            onDropAccepted={ onDropAccepted }
            onDropRejected={ onDropRejected }
            disabled={ disabled }
            accept={ acceptedMimeTypes }
            multiple={ false }
        >
            {(props) => <DropzoneRender
                acceptedFileExtensions={ acceptedFileExtensions }
                isUploading={ isUploading }
                { ...baseProps }
                { ...props }
            /> }
        </Dropzone>
    );
}

DropzoneInput.propTypes = {
    acceptedFileExtensions: PropTypes.arrayOf(PropTypes.string).isRequired,
    isUploading: PropTypes.bool,
    disabled: PropTypes.bool,
    onFileSelected: PropTypes.func.isRequired,
};
DropzoneInput.defaultProps = {
    isUploading: false,
    disabled: false,
};


function DropzoneRender({
    isDragAccept,
    isDragReject,
    isDragActive,
    getRootProps,
    getInputProps,
    acceptedFileExtensions,
    icon,
    textRejectedMultipleFile,
    textRejected,
    textAccepted,
    label,
}) {
    const { t } = useTranslation();

    const className = `dropZoneStyle ${isDragReject ? 'dropZoneStyleRejected' : '' } ${isDragAccept ? 'dropZoneStyleValidate' : ''}`;

    let label_ = label;
    if (isDragActive) {
        if (isDragReject) {
            label_ = textRejectedMultipleFile || t(["nacre.UploadButton.text-rejected-multiple-file", "Only one file accepted"]);
        } else {
            label_ = textAccepted || t(["nacre.UploadButton.text-accepted", "File is accepted"]);
        }
    } else if (isDragReject) {
        label_ = textRejected || t(["nacre.UploadButton.text-rejected", "File type not accepted"]);
    }
    const icon_ = getIcon(isDragAccept, isDragReject) || icon;

    return (
        <div className={ className } { ...getRootProps() } >
            <input { ...getInputProps() } />
            <div>
                { icon ? <p> <Icon size="huge" name={ icon_ } /> </p> : null }
                <p> { label_ }</p>
            </div>
            <div className="typeFile">
                <Trans
                    i18nKey="nacre.UploadButton.text-allowed-file-extensions"
                    count={ acceptedFileExtensions.length }
                    values={ {
                        type: acceptedFileExtensions.join(', ')
                    } }
                >Only the file type (%type%) is allowed.</Trans>
            </div>
        </div>
    );
}

DropzoneRender.propTypes = {
    icon: PropTypes.string,
    isDragReject: PropTypes.bool.isRequired,
    isDragAccept: PropTypes.bool.isRequired,
    isDragActive: PropTypes.bool,
    getRootProps: PropTypes.func.isRequired,
    getInputProps: PropTypes.func.isRequired,
    acceptedFileExtensions: PropTypes.arrayOf(PropTypes.string).isRequired,
    textRejectedMultipleFile: PropTypes.node,
    textRejected: PropTypes.node,
    textAccepted: PropTypes.node,
    label: PropTypes.node.isRequired,
};
DropzoneRender.defaultProps = {
    isDragActive: false,
    icon: '',
};

function getIcon(accept, reject) {
    if (reject) {
        return 'ban';
    } else if (accept) {
        return 'check';
    }
}
